<template>
    <div class="dashboard">
        <!-- 统计卡片 -->
        <div class="stats-grid">
            <div class="stat-card" v-for="(stat, index) in stats" :key="index"
                :style="{ animationDelay: `${index * 0.1}s` }">
                <div class="stat-icon" :class="`icon-${stat.color}`">
                    <component :is="stat.icon" />
                </div>
                <div class="stat-info">
                    <h4 class="stat-title">{{ stat.title }}</h4>
                    <p class="stat-number">{{ stat.value }}</p>
                    <span class="stat-change" :class="stat.trend">{{ stat.change }}</span>
                </div>
            </div>
        </div>

        <!-- 图表和数据区域 -->
        <div class="content-grid">
            <div class="chart-card large">
                <div class="card-header">
                    <h3>会员增长趋势</h3>
                    <div class="card-actions">
                        <button v-for="p in ['周', '月', '年']" :key="p" class="btn-filter"
                            :class="{ active: activePeriod === p }" @click="activePeriod = p">{{ p }}</button>
                    </div>
                </div>
                <div class="chart-placeholder">
                    <div class="line-chart">
                        <svg viewBox="0 0 700 200" preserveAspectRatio="none" class="line-svg">
                            <defs>
                                <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stop-color="var(--primary-blue)" stop-opacity="0.25" />
                                    <stop offset="100%" stop-color="var(--primary-blue)" stop-opacity="0" />
                                </linearGradient>
                            </defs>
                            <!-- 横向网格线 -->
                            <line x1="0" y1="40" x2="700" y2="40" stroke="var(--border-color)" stroke-width="1" />
                            <line x1="0" y1="90" x2="700" y2="90" stroke="var(--border-color)" stroke-width="1" />
                            <line x1="0" y1="140" x2="700" y2="140" stroke="var(--border-color)" stroke-width="1" />
                            <line x1="0" y1="190" x2="700" y2="190" stroke="var(--border-color)" stroke-width="1" />
                            <!-- 填充区域 -->
                            <polygon
                                :points="areaPoints"
                                fill="url(#lineGrad)"
                            />
                            <!-- 折线 -->
                            <polyline
                                :points="linePoints"
                                fill="none"
                                stroke="var(--primary-blue)"
                                stroke-width="2.5"
                                stroke-linejoin="round"
                                stroke-linecap="round"
                            />
                            <!-- 数据点 -->
                            <circle
                                v-for="(pt, i) in pointCoords" :key="i"
                                :cx="pt.x" :cy="pt.y" r="4"
                                fill="white" stroke="var(--primary-blue)" stroke-width="2.5"
                            />
                        </svg>
                        <!-- X 轴标签 -->
                        <div class="line-labels">
                            <span v-for="(bar, i) in chartBars" :key="i" class="line-label">{{ bar.l }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="chart-card">
                <div class="card-header">
                    <h3>{{ scheduleTitle }}</h3>
                    <span class="see-all" @click="$router.push('/appointment')">查看全部</span>
                </div>
                <div v-if="todaySchedule.length === 0" class="schedule-empty">
                    <span>今日暂无课程安排</span>
                </div>
                <div v-else class="timeline">
                    <div class="tl-item" v-for="(t, i) in todaySchedule" :key="i">
                        <div class="tl-time">{{ t.time }}</div>
                        <div class="tl-dot" :style="{ background: t.color }"></div>
                        <div class="tl-content">
                            <p class="tl-course">{{ t.course }}</p>
                            <p class="tl-detail">{{ t.coach }} · <span :class="`tl-status-${t.statusKey}`">{{ t.statusLabel }}</span></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { UserFilled, Stopwatch, Notebook, User } from '@element-plus/icons-vue';
import api from '@/api/request';

const apptColors = ['#0062FF', '#10B981', '#F59E0B', '#8B5CF6', '#06B6D4', '#EC4899'];
const apptStatusMap = { 0: '待确认', 1: '已确认', 2: '已取消', 3: '已完成', 4: '已拒绝' };
const apptStatusKeyMap = { 0: 'pending', 1: 'confirmed', 2: 'cancelled', 3: 'done', 4: 'rejected' };

const adminStatTemplate = [
    { title: '总会员数', value: '-', change: '', trend: 'up', icon: 'UserFilled', color: 'orange' },
    { title: '今日打卡', value: '-', change: '', trend: 'up', icon: 'Stopwatch', color: 'red' },
    { title: '活跃课程', value: '-', change: '', trend: 'up', icon: 'Notebook', color: 'yellow' },
    { title: '在职教练', value: '-', change: '', trend: 'neutral', icon: 'User', color: 'coral' },
];
const apptStatTemplate = [
    { title: '待确认', value: '-', change: '', trend: 'up', icon: 'Stopwatch', color: 'orange' },
    { title: '已确认', value: '-', change: '', trend: 'up', icon: 'UserFilled', color: 'red' },
    { title: '已取消', value: '-', change: '', trend: 'neutral', icon: 'Notebook', color: 'yellow' },
    { title: '总预约', value: '-', change: '', trend: 'up', icon: 'User', color: 'coral' },
];

export default {
    name: 'HomeView',
    components: { UserFilled, Stopwatch, Notebook, User },
    computed: {
        userRole() {
            return this.$store.getters.userRole;
        },
        isApptDashboard() {
            return this.userRole === 'coach' || this.userRole === 'member';
        },
        scheduleTitle() {
            return this.isApptDashboard ? '我的今日课程' : '今日课程安排';
        },
        gymName() {
            return this.$store.getters.gymName;
        },
        gymLogo() {
            return this.$store.getters.gymLogo;
        },
        pointCoords() {
            const svgW = 700, svgH = 200, padX = 50, padY = 20;
            const maxV = Math.max(...this.chartBars.map(b => b.v));
            const n = this.chartBars.length;
            return this.chartBars.map((b, i) => ({
                x: padX + i * ((svgW - padX * 2) / (n - 1)),
                y: padY + (1 - b.v / maxV) * (svgH - padY * 2)
            }));
        },
        linePoints() {
            return this.pointCoords.map(p => `${p.x},${p.y}`).join(' ');
        },
        areaPoints() {
            const pts = this.pointCoords;
            const bottom = 190;
            const first = pts[0], last = pts[pts.length - 1];
            return [
                ...pts.map(p => `${p.x},${p.y}`),
                `${last.x},${bottom}`,
                `${first.x},${bottom}`
            ].join(' ');
        }
    },
    data() {
        return {
            activePeriod: '周',
            stats: adminStatTemplate.map((s) => ({ ...s })),
            chartBars: [
                { h: 55, v: 220, l: '周一' },
                { h: 72, v: 290, l: '周二' },
                { h: 48, v: 190, l: '周三' },
                { h: 90, v: 360, l: '周四' },
                { h: 65, v: 260, l: '周五' },
                { h: 100, v: 400, l: '周六' },
                { h: 82, v: 328, l: '周日' },
            ],
            todaySchedule: [],
        };
    },
    mounted() {
        this.applyStatTemplate();
        this.loadStats();
        this.loadTodaySchedule();
        window.addEventListener('gym-refresh-appointments', this.loadTodaySchedule);
    },
    beforeUnmount() {
        window.removeEventListener('gym-refresh-appointments', this.loadTodaySchedule);
    },
    methods: {
        applyStatTemplate() {
            const tpl = this.isApptDashboard ? apptStatTemplate : adminStatTemplate;
            this.stats = tpl.map((s) => ({ ...s }));
        },
        async loadStats() {
            try {
                const res = await api.getDashboardStats();
                if (res.data && res.data.code === 200) {
                    const d = res.data.data;
                    if (this.isApptDashboard) {
                        this.stats[0].value = String(d.pending ?? 0);
                        this.stats[1].value = String(d.confirmed ?? 0);
                        this.stats[2].value = String(d.cancelled ?? 0);
                        this.stats[3].value = String(d.total ?? 0);
                    } else {
                        this.stats[0].value = d.totalMembers?.toString() || '0';
                        this.stats[1].value = d.todayCheckins?.toString() || '0';
                        this.stats[2].value = d.activeCourses?.toString() || '0';
                        this.stats[3].value = d.totalCoaches?.toString() || '0';
                    }
                }
            } catch (e) {
                // 保留默认占位数据不影响页面展示
            }
        },
        async loadTodaySchedule() {
            try {
                const today = (() => {
                    const t = new Date();
                    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
                })();
                const res = await api.getAppointmentPage({ pageNumber: 1, pageSize: 100 });
                if (res.data?.code === 200) {
                    const records = res.data.data?.records || [];
                    this.todaySchedule = records
                        .filter(a => a.appointmentTime && String(a.appointmentTime).startsWith(today))
                        .sort((a, b) => String(a.appointmentTime).localeCompare(String(b.appointmentTime)))
                        .map((a, i) => ({
                            time: String(a.appointmentTime).substring(11, 16),
                            course: a.courseName || `课程${a.courseId}`,
                            coach: a.coachName || `教练${a.coachId}`,
                            statusLabel: apptStatusMap[a.status] ?? '未知',
                            statusKey: apptStatusKeyMap[a.status] || 'pending',
                            color: apptColors[i % apptColors.length],
                        }));
                }
            } catch (e) {
                // 静默失败
            }
        },
    }
};
</script>

<style scoped>
.dashboard {
    animation: fadeInUp 0.4s ease both;
}

/* 统计卡片 */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
}

.stat-card {
    background: var(--bg-white);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    transition: all 0.3s ease;
    animation: fadeInUp 0.6s ease forwards;
    opacity: 0;
    position: relative;
    overflow: hidden;
}

.stat-card::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 80px;
    height: 80px;
    background: radial-gradient(circle, rgba(0, 98, 255, 0.05) 0%, transparent 70%);
    border-radius: 50%;
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
    border-color: var(--primary-blue);
}

.stat-icon {
    width: 56px;
    height: 56px;
    border-radius: var(--radius-lg);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    flex-shrink: 0;
}

.stat-icon.icon-orange {
    background: rgba(0, 98, 255, 0.1);
    color: var(--primary-blue);
}

.stat-icon.icon-red {
    background: rgba(16, 185, 129, 0.1);
    color: var(--success-green);
}

.stat-icon.icon-yellow {
    background: rgba(245, 158, 11, 0.1);
    color: var(--warning-orange);
}

.stat-icon.icon-coral {
    background: rgba(6, 182, 212, 0.1);
    color: var(--info-cyan);
}

.stat-info {
    flex: 1;
}

.stat-title {
    font-size: 13px;
    color: var(--text-secondary);
    margin: 0 0 6px;
    font-weight: 500;
}

.stat-number {
    font-size: 28px;
    font-weight: 700;
    color: var(--text-primary);
    margin: 0 0 6px;
    font-family: 'Inter', sans-serif;
    line-height: 1;
}

.stat-change {
    font-size: 12px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: var(--radius-sm);
    display: inline-block;
}

.stat-change.up {
    background: rgba(16, 185, 129, 0.1);
    color: var(--success-green);
}

.stat-change.neutral {
    background: rgba(107, 114, 128, 0.1);
    color: var(--text-secondary);
}

/* 内容网格 */
.content-grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 20px;
}

.chart-card {
    background: var(--bg-white);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    padding: 20px;
    animation: fadeInUp 0.6s ease 0.3s forwards;
    opacity: 0;
}

.card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
}

.card-header h3 {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
}

.see-all {
    font-size: 12px;
    color: var(--primary-blue);
    cursor: pointer;
    font-weight: 500;
}

.see-all:hover {
    text-decoration: underline;
}

.card-actions {
    display: flex;
    gap: 6px;
}

.btn-filter {
    padding: 5px 14px;
    border: 1px solid var(--border-color);
    background: var(--bg-gray);
    color: var(--text-secondary);
    border-radius: var(--radius-sm);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
}

.btn-filter:hover {
    border-color: var(--primary-blue);
    color: var(--primary-blue);
    background: var(--bg-white);
}

.btn-filter.active {
    background: var(--primary-blue);
    border-color: var(--primary-blue);
    color: white;
}

/* 折线图 */
.chart-placeholder {
    height: 260px;
    display: flex;
    flex-direction: column;
    padding: 8px 0 0;
}

.line-chart {
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
}

.line-svg {
    flex: 1;
    width: 100%;
    height: 100%;
    overflow: visible;
}

.line-labels {
    display: flex;
    justify-content: space-between;
    padding: 6px 50px 0;
}

.line-label {
    font-size: 11px;
    color: var(--text-muted);
    text-align: center;
    flex: 1;
}

/* 今日课程安排时间线 */
.schedule-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 160px;
    font-size: 13px;
    color: var(--text-muted);
}

.timeline {
    display: flex;
    flex-direction: column;
    gap: 0;
    max-height: 260px;
    overflow-y: auto;
}

.tl-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding-bottom: 16px;
    position: relative;
}

.tl-item:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 40px;
    top: 18px;
    bottom: 0;
    width: 1px;
    background: var(--border-color);
}

.tl-time {
    font-size: 11px;
    font-weight: 700;
    color: var(--text-muted);
    font-family: 'Inter', sans-serif;
    width: 34px;
    padding-top: 2px;
    flex-shrink: 0;
}

.tl-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 4px;
    box-shadow: 0 0 0 3px rgba(0,0,0,0.06);
}

.tl-content { flex: 1; min-width: 0; }

.tl-course {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.tl-detail {
    font-size: 11px;
    color: var(--text-muted);
    margin: 0;
}

.tl-status-pending   { color: #F59E0B; }
.tl-status-confirmed { color: #10B981; }
.tl-status-cancelled { color: #9CA3AF; }
.tl-status-done      { color: #0062FF; }
.tl-status-rejected  { color: #EF4444; }

@media (max-width: 1200px) {
    .content-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }
    .chart-placeholder {
        height: 200px;
    }
}

@media (max-width: 480px) {
    .stats-grid {
        grid-template-columns: 1fr;
    }
    .card-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
    .card-actions {
        width: 100%;
        justify-content: flex-start;
    }
}
</style>